import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Search,
  Layers,
  Trash2,
  CheckSquare,
  Square,
  ArrowRight,
  BoxSelect,
  Plus,
  PackagePlus,
  Hash,
  Printer,
  BarChart2,
  CheckCircle,
  RotateCcw,
  Hammer,
  ClipboardList,
  TrendingUp,
  Flame,
} from "lucide-react";
import { stockProductionService } from "../services/stockProductionService";
import { loteService } from "../services/loteService";
import { formatProductionTicket } from "../utils/printFormatter"; // Importe o formatador
import TextModal from "../components/modals/TextModal";
import SkuConfirmationModal from "../components/modals/SkuConfirmationModal";
import { logAction } from "../services/logService";
import StockProductionItemRow from "../components/production/StockProductionItemRow";
import { pb } from "../services/pocketbase";
import { suppliesService } from "../services/suppliesService";
import { useOperator } from "../contexts/OperatorContext";
import LotesTab from "./LotesTab";
import MotivosConfigTab from "./MotivosConfigTab";

// CONFIGURAÇÃO DOS STATUS PADRÃO
const DEFAULT_STATUSES = [
  {
    id: "pe_solicitado",
    label: "Solicitado",
    color: "border-l-blue-500",
    bg: "bg-blue-50",
    text: "text-blue-700",
  },
  {
    id: "pe_imprimindo",
    label: "Imprimindo",
    color: "border-l-purple-500",
    bg: "bg-purple-50",
    text: "text-purple-700",
  },
  {
    id: "pe_cura",
    label: "Cura",
    color: "border-l-indigo-400",
    bg: "bg-indigo-100",
    text: "text-indigo-800",
  },
  {
    id: "pe_fundicao",
    label: "Fundição",
    color: "border-l-orange-500",
    bg: "bg-orange-50",
    text: "text-orange-700",
  },
  {
    id: "pe_fundidos",
    label: "Fundidos",
    color: "border-l-amber-600",
    bg: "bg-amber-50",
    text: "text-amber-800",
  },
  {
    id: "pe_pre_escapulario",
    label: "Pré-Escapulário",
    color: "border-l-pink-500",
    bg: "bg-pink-50",
    text: "text-pink-700",
  },
  {
    id: "pe_conferencia",
    label: "Conferência",
    color: "border-l-teal-500",
    bg: "bg-teal-50",
    text: "text-teal-700",
  },
  {
    id: "pe_acabamento",
    label: "Acab. externo",
    color: "border-l-indigo-500",
    bg: "bg-indigo-50",
    text: "text-indigo-700",
  },
  {
    id: "pe_cravacao",
    label: "Cravação",
    color: "border-l-rose-500",
    bg: "bg-rose-50",
    text: "text-rose-700",
  },
  {
    id: "pe_polimento",
    label: "Polimento",
    color: "border-l-yellow-500",
    bg: "bg-yellow-50",
    text: "text-yellow-700",
  },
  {
    id: "pe_revisao",
    label: "Revisão",
    color: "border-l-red-500",
    bg: "bg-red-50",
    text: "text-red-700",
  },
  {
    id: "pe_finalizacao",
    label: "Finalização",
    color: "border-l-blue-400",
    bg: "bg-blue-50",
    text: "text-blue-600",
  },
  {
    id: "pe_suspenso",
    label: "Suspenso",
    color: "border-l-red-500",
    bg: "bg-red-50",
    text: "text-red-700",
  },
];

// Helper Inteligente de Cor de Pedra
const getStoneColor = (catalogItem) => {
  if (!catalogItem) return "Não Definido";
  
  // 1. Tenta buscar no array/JSON de tags (Estrutura iSet)
  const tagsSource = catalogItem.tags || catalogItem.full_data?.tags;
  if (tagsSource) {
    try {
      let tagsArray = typeof tagsSource === "string"
        ? JSON.parse(tagsSource.replace(/\\"/g, '"'))
        : tagsSource;
      
      if (Array.isArray(tagsArray)) {
        const stoneColorTag = tagsArray.find(t => 
          t.group === "Cor da Pedra" || 
          t.group === "Cor da pedra" || 
          t.group === "Cor" || 
          t.group === "cor"
        );
        if (stoneColorTag && stoneColorTag.value && typeof stoneColorTag.value === "string" && stoneColorTag.value.trim() !== "") {
          return stoneColorTag.value.trim();
        }
      }
    } catch (e) {
      // Falha silenciosa de parse
    }
  }

  // 2. Fallback para chaves diretas ou flat no full_data
  const data = catalogItem.full_data || catalogItem;
  const fields = ["Cor da Pedra", "Cor", "cor", "stoneColor", "stone_color", "Cor da pedra", "COR"];
  for (const field of fields) {
    if (data[field] && typeof data[field] === "string" && data[field].trim() !== "") {
      return data[field].trim();
    }
  }
  
  return "Não Definido";
};

// Mapeador de estilos visuais suaves para cada cor de pedra
const getStoneColorStyles = (color) => {
  if (!color) return { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-600", dot: "bg-slate-400" };
  
  const norm = color.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  
  if (norm.includes("azul") || norm.includes("sky")) {
    return { bg: "bg-blue-50/70 hover:bg-blue-50", border: "border-blue-200/80", text: "text-blue-800", dot: "bg-blue-500" };
  }
  if (norm.includes("vermelh") || norm.includes("rubi") || norm.includes("laranja")) {
    return { bg: "bg-red-50/70 hover:bg-red-50", border: "border-red-200/80", text: "text-red-800", dot: "bg-red-500" };
  }
  if (norm.includes("rosa") || norm.includes("pink")) {
    return { bg: "bg-pink-50/70 hover:bg-pink-50", border: "border-pink-200/80", text: "text-pink-800", dot: "bg-pink-500" };
  }
  if (norm.includes("verd") || norm.includes("peridoto") || norm.includes("esmeralda")) {
    return { bg: "bg-emerald-50/70 hover:bg-emerald-50", border: "border-emerald-200/80", text: "text-emerald-800", dot: "bg-emerald-500" };
  }
  if (norm.includes("rox") || norm.includes("lilas") || norm.includes("ametista")) {
    return { bg: "bg-purple-50/70 hover:bg-purple-50", border: "border-purple-200/80", text: "text-purple-800", dot: "bg-purple-500" };
  }
  if (norm.includes("amarel") || norm.includes("ouro")) {
    return { bg: "bg-amber-50/70 hover:bg-amber-50", border: "border-amber-200/80", text: "text-amber-800", dot: "bg-amber-500" };
  }
  if (norm.includes("pret") || norm.includes("onix")) {
    return { bg: "bg-slate-100 hover:bg-slate-100/80", border: "border-slate-300", text: "text-slate-900", dot: "bg-slate-900" };
  }
  if (norm.includes("branc") || norm.includes("cristal") || norm.includes("transparente")) {
    return { bg: "bg-slate-50/80 hover:bg-slate-50", border: "border-slate-200", text: "text-slate-700", dot: "bg-slate-300" };
  }
  
  // Default fallback
  return { bg: "bg-slate-50/70 hover:bg-slate-50", border: "border-slate-200/80", text: "text-slate-600", dot: "bg-slate-400" };
};

export default function StockProductionTab({ user, findCatalogItem, inventory, canceledLoteIds = new Set(), onRefresh }) {
  const { isSharedTerminal, terminalType, activeOperator } = useOperator();
  const isOficina = isSharedTerminal && terminalType === "OFICINA";

  // Helper para decodificar status customizado armazenado em 'reason'
  const getItemStatus = useCallback((item) => {
    if (item.situacao_fluxo === "suspenso") {
      return "pe_suspenso";
    }
    let status = item.status;
    if (item.reason && item.reason.startsWith("pe_")) {
      status = item.reason;
    }
    // Salvaguarda contra erros de digitação no banco (ex: pc_imprimindo -> pe_imprimindo)
    if (status && status.startsWith("pc_")) {
      status = "pe_" + status.substring(3);
    }
    return status;
  }, []);

  const parseStatusId = useCallback((id) => {
    if (!id || !id.startsWith("pe_")) return null;
    const parts = id.split("_");
    if (parts.length <= 1) return null;
    
    // Se a última parte for timestamp (número com mais de 8 dígitos)
    const lastIsTimestamp = !isNaN(Number(parts[parts.length - 1])) && parts[parts.length - 1].length > 8;
    const labelParts = lastIsTimestamp ? parts.slice(1, -1) : parts.slice(1);
    
    const labelNormalized = labelParts.join(" ");
    if (!labelNormalized) return "Personalizado";
    return labelNormalized.charAt(0).toUpperCase() + labelNormalized.slice(1);
  }, []);

  // Carrega status salvos ou os padrão
  const [statuses, setStatuses] = useState(() => {
    const saved = localStorage.getItem("pe_custom_statuses");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      } catch (e) {
        console.error("Erro parsing saved statuses:", e);
      }
    }
    return DEFAULT_STATUSES;
  });

  // Salva no localStorage quando o status muda
  useEffect(() => {
    localStorage.setItem("pe_custom_statuses", JSON.stringify(statuses));
  }, [statuses]);

  const [items, setItems] = useState([]);

  // Sincroniza dinamicamente status customizados ativos no banco de dados para todas as máquinas
  useEffect(() => {
    if (items.length > 0) {
      setStatuses((prevStatuses) => {
        let updated = false;
        const nextStatuses = [...prevStatuses];
        
        items.forEach((item) => {
          if (item.reason && item.reason.startsWith("pe_")) {
            const statusId = item.reason;
            if (!nextStatuses.some((s) => s.id === statusId)) {
              const label = parseStatusId(statusId);
              if (label) {
                nextStatuses.push({
                  id: statusId,
                  label: label,
                  color: "border-l-slate-400",
                  bg: "bg-slate-50",
                  text: "text-slate-700",
                });
                updated = true;
              }
            }
          }
        });
        
        return updated ? nextStatuses : prevStatuses;
      });
    }
  }, [items, parseStatusId]);

  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem("pe_custom_statuses");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0 && parsed[0]?.id) {
          return parsed[0].id;
        }
      } catch {}
    }
    return "pe_solicitado";
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [stoneColorFilter, setStoneColorFilter] = useState("all");
  const [newStatusLabel, setNewStatusLabel] = useState("");

  // Estado para Adicionar Novo
  const [newSku, setNewSku] = useState("");
  const [newQty, setNewQty] = useState("1");
  const [isAdding, setIsAdding] = useState(false);

  // Estados de BOM e Supplies
  const [bomsList, setBomsList] = useState([]);
  const [suppliesList, setSuppliesList] = useState([]);
  const [bomSkuSet, setBomSkuSet] = useState(new Set());
  const [loadingData, setLoadingData] = useState(true);

  // Estados Locais para Governança por Lotes
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [requestSku, setRequestSku] = useState("");
  const [requestQty, setRequestQty] = useState("1");
  const [requestMotivo, setRequestMotivo] = useState("");

  const [isSuspendModalOpen, setIsSuspendModalOpen] = useState(false);
  const [suspendItemId, setSuspendItemId] = useState(null);
  const [suspendMotivoId, setSuspendMotivoId] = useState("");
  const [suspendObs, setSuspendObs] = useState("");
  const [suspensaoMotivosList, setSuspensaoMotivosList] = useState([]);

  const loadData = useCallback(async () => {
    try {
      setLoadingData(true);
      const [bomRecords, suppliesRecords, motivosRecords] = await Promise.all([
        pb.collection("sku_bom").getFullList({ fields: "sku,supply_id,quantity_per_unit" }),
        pb.collection("supplies").getFullList({ sort: "name" }),
        pb.collection("estoque_suspensao_motivos").getFullList({ filter: "ativo = true", sort: "nome" }).catch(() => [])
      ]);
      setBomsList(bomRecords);
      setSuppliesList(suppliesRecords);
      setBomSkuSet(new Set(bomRecords.map(r => r.sku.trim().toUpperCase())));
      setSuspensaoMotivosList(motivosRecords || []);
    } catch (e) {
      console.error("Erro ao carregar dados de BOM/Supplies:", e);
    } finally {
      setLoadingData(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Estados para Seleção em Massa
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [targetStatus, setTargetStatus] = useState("");

  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printContent, setPrintContent] = useState("");

  const [isSkuModalOpen, setIsSkuModalOpen] = useState(false);
  const [skuModalItems, setSkuModalItems] = useState([]);

  // --- ATUALIZAÇÃO REATIVA (VIA PROP) ---
  useEffect(() => {
    // Filtra apenas o que é factory stock
    if (inventory) {
      const factoryItems = inventory.filter(i => {
        if (!i.isPE) return false;
        if (["adjusted_out", "sold", "pe_interceptado"].includes(i.status)) return false;
        return true; // Exibe tudo, inclusive os suspensos (que são roteados para a aba pe_suspenso)
      });
      setItems(factoryItems);
    }
  }, [inventory]);

  // --- FILTROS E ORDENAÇÃO ---
  const filteredItems = useMemo(() => {
    return items
      .filter((i) => getItemStatus(i) === activeTab)
      .filter((i) => {
        const catalogItem = findCatalogItem ? findCatalogItem(i.sku) : null;
        const matchesColor = stoneColorFilter === "all" || getStoneColor(catalogItem) === stoneColorFilter;
        if (!matchesColor) return false;

        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
          i.sku?.toLowerCase().includes(search) ||
          i.name?.toLowerCase().includes(search)
        );
      })
      .sort((a, b) => {
        // Ordenação por 'created' default
        const timeA = a.created ? new Date(a.created).getTime() : 0;
        const timeB = b.created ? new Date(b.created).getTime() : 0;
        return timeB - timeA;
      });
  }, [items, activeTab, searchTerm, stoneColorFilter, findCatalogItem, getItemStatus]);

  const groupedByColor = useMemo(() => {
    const groups = {};
    filteredItems.forEach((item) => {
      const catalogItem = findCatalogItem ? findCatalogItem(item.sku) : null;
      const color = getStoneColor(catalogItem);
      if (!groups[color]) groups[color] = [];
      groups[color].push(item);
    });
    return groups;
  }, [filteredItems, findCatalogItem]);

  const sortedColors = useMemo(() => {
    const keys = Object.keys(groupedByColor);
    return keys.sort((a, b) => {
      if (a === "Não Definido") return 1;
      if (b === "Não Definido") return -1;
      return a.localeCompare(b);
    });
  }, [groupedByColor]);

  const availableColors = useMemo(() => {
    const colors = new Set();
    items.forEach(item => {
      if (getItemStatus(item) === activeTab) {
        const catalogItem = findCatalogItem ? findCatalogItem(item.sku) : null;
        colors.add(getStoneColor(catalogItem));
      }
    });
    return Array.from(colors).sort();
  }, [items, activeTab, findCatalogItem, getItemStatus]);

  const counts = useMemo(() => {
    const c = {};
    statuses.forEach((s) => (c[s.id] = 0));
    items.forEach((i) => {
      const itemStatus = getItemStatus(i);
      if (c[itemStatus] !== undefined) c[itemStatus]++;
    });
    return c;
  }, [items, statuses, getItemStatus]);

  // --- GERENCIADOR DE STATUS ---
  const handleAddStatus = (e) => {
    e.preventDefault();
    const labelClean = newStatusLabel.trim();
    if (!labelClean) return;
    const id = `pe_${labelClean.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "_")}_${Date.now()}`;
    
    if (statuses.some(s => s.id === id)) {
      alert("Este status já existe.");
      return;
    }
    
    const newStatus = {
      id,
      label: labelClean,
      color: "border-l-slate-400",
      bg: "bg-slate-50",
      text: "text-slate-700",
    };
    
    setStatuses(prev => [...prev, newStatus]);
    setNewStatusLabel("");
    alert(`Status "${labelClean}" adicionado com sucesso!`);
  };

  const handleRemoveStatus = (statusId, e) => {
    if (e) e.stopPropagation();
    if (counts[statusId] > 0) {
      alert(`Não é possível remover o status "${statuses.find(s => s.id === statusId)?.label}" porque existem ${counts[statusId]} itens nele.`);
      return;
    }
    if (!window.confirm("Deseja realmente remover este status?")) return;
    
    const newStatuses = statuses.filter(s => s.id !== statusId);
    setStatuses(newStatuses);
    if (activeTab === statusId) {
      setActiveTab(newStatuses[0]?.id || "pe_solicitado");
    }
  };

  // --- AÇÕES ---

  const handleAddItem = async (e) => {
    e.preventDefault();
    const skuClean = newSku.trim().toUpperCase();
    const qty = parseInt(newQty);

    if (!skuClean) return alert("Digite o SKU.");
    if (!qty || qty < 1) return alert("Quantidade inválida.");

    setIsAdding(true);

    try {
      await stockProductionService.launchBatch(skuClean, qty, user?.name);

      logAction(
        user,
        "ESTOQUE_PE",
        "CRIAR",
        `Criou ${qty} novos lotes do SKU: ${skuClean}`,
        { sku: skuClean, qty: qty }
      );
      setNewSku("");
      setNewQty("1");
      setActiveTab("pe_solicitado");
      loadData(); // Atualiza insumos/BOM
    } catch (err) {
      console.error(err);
      alert("Erro ao criar: " + err.message);
    } finally {
      setIsAdding(false);
    }
  };

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === filteredItems.length && filteredItems.length > 0) {
      setSelectedIds(new Set());
    } else {
      const allIds = filteredItems.map((i) => i.id);
      setSelectedIds(new Set(allIds));
    }
  }, [selectedIds.size, filteredItems]);

  const toggleSelectOne = useCallback((id) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  }, []);

  const handleBulkMove = useCallback(async () => {
    if (selectedIds.size === 0 || !targetStatus) return;
    if (!window.confirm(`Mover ${selectedIds.size} itens?`)) return;

    let previousItems = [];
    setItems((prev) => {
      previousItems = [...prev];
      return prev.map(item => {
        if (selectedIds.has(item.id)) {
          return { ...item, status: targetStatus, updated: new Date().toISOString() };
        }
        return item;
      });
    });

    try {
      await stockProductionService.advanceBatchStatus(Array.from(selectedIds), targetStatus, user?.name);

      logAction(
        user,
        "ESTOQUE_PE",
        "MOVER_EM_MASSA",
        `Moveu ${selectedIds.size} itens para o status: ${targetStatus}`,
        {
          count: selectedIds.size,
          targetStatus: targetStatus,
          itemIds: Array.from(selectedIds),
        }
      );

      setSelectedIds(new Set());
      setTargetStatus("");
      loadData(); // Atualiza insumos/BOM
    } catch (err) {
      if (previousItems.length > 0) setItems(previousItems);
      console.error(err);
      alert("Erro ao mover: " + err.message);
    }
  }, [selectedIds, targetStatus, user, loadData]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm("Remover este item?")) return;

    const itemToDelete = items.find((i) => i.id === id);
    const sku = itemToDelete?.sku || "Desconhecido";

    let previousItems = [];
    setItems((prev) => {
      previousItems = [...prev];
      return prev.filter(i => i.id !== id);
    });

    try {
      await stockProductionService.deleteBatch([id]); 

      logAction(
        user,
        "ESTOQUE_PE",
        "EXCLUIR",
        `Apagou o item ${sku}`,
        { deletedId: id, sku: sku }
      );
      loadData(); // Atualiza insumos/BOM
    } catch (e) {
      if (previousItems.length > 0) setItems(previousItems);
      alert("Erro ao deletar: " + e.message);
    }
  }, [items, user, loadData]);

  const handleConvertLoteToAvulso = useCallback(async (itemId) => {
    if (!window.confirm("Confirmar a desvinculação deste lote cancelado? A peça continuará a ser produzida como Avulsa.")) return;
    try {
      await stockProductionService.convertToAvulso(itemId, user?.name || "Fábrica");
      alert("Peça convertida para Avulsa com sucesso!");
      if (onRefresh) onRefresh();
    } catch (e) {
      alert("Erro ao converter peça para Avulsa: " + e.message);
    }
  }, [user, onRefresh]);

  const handleReactivateItem = useCallback(async (itemId) => {
    if (!window.confirm("Confirmar a reativação desta peça? Isso irá redebitar os insumos da receita BOM.")) return;
    try {
      setLoadingData(true);
      await stockProductionService.reactivateItem(itemId, user?.name || "Fábrica");
      alert("Peça reativada com sucesso!");
      if (onRefresh) onRefresh();
    } catch (e) {
      alert("Erro ao reativar peça: " + e.message);
    } finally {
      setLoadingData(false);
    }
  }, [user, onRefresh]);

  const handleOpenSuspendModal = useCallback((id) => {
    setSuspendItemId(id);
    setSuspendMotivoId("");
    setSuspendObs("");
    setIsSuspendModalOpen(true);
  }, []);

  const handleSuspendConfirm = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (!suspendItemId || !suspendMotivoId) return alert("Selecione um motivo de suspensão.");

    const selectedMotivo = suspensaoMotivosList.find(m => m.id === suspendMotivoId || m.nome === suspendMotivoId);
    if (selectedMotivo?.obs_obrigatoria && !suspendObs.trim()) {
      return alert("Este motivo exige uma observação descritiva.");
    }

    try {
      // Usamos setItems para atualizar localmente imediato
      setItems((prev) => prev.filter(i => i.id !== suspendItemId));
      
      const userLabel = user?.name || "Sistema";
      await stockProductionService.suspendItem(suspendItemId, selectedMotivo?.nome || suspendMotivoId, suspendObs, userLabel);
      
      logAction(
        user,
        "ESTOQUE_PE",
        "SUSPENDER",
        `Suspendeu item ${suspendItemId}. Motivo: ${selectedMotivo?.nome || suspendMotivoId}`,
        { itemId: suspendItemId, motivo: selectedMotivo?.nome || suspendMotivoId }
      );

      alert("Peça suspensa com sucesso! Os insumos BOM foram estornados.");
      setIsSuspendModalOpen(false);
      setSuspendItemId(null);
      setSuspendMotivoId("");
      setSuspendObs("");
      loadData();
    } catch (err) {
      console.error(err);
      alert("Erro ao suspender peça: " + err.message);
    }
  }, [suspendItemId, suspendMotivoId, suspendObs, suspensaoMotivosList, user, loadData]);

  const handleRequestSubmit = useCallback(async (e) => {
    if (e) e.preventDefault();
    const sku = requestSku.trim().toUpperCase();
    const qty = parseInt(requestQty) || 0;
    const motivo = requestMotivo.trim();

    if (!sku) return alert("Informe o SKU.");
    if (qty <= 0) return alert("Quantidade deve ser maior que zero.");
    if (!motivo) return alert("Informe o motivo da solicitação.");

    try {
      const operatorName = activeOperator?.nome || user?.name || "Oficina";
      await loteService.requestLoteFromOficina([{ sku, qtd: qty }], operatorName, motivo);
      
      logAction(
        user,
        "ESTOQUE_PE",
        "SOLICITAR_OFICINA",
        `Oficina solicitou ${qty} un de ${sku}. Motivo: ${motivo}`,
        { sku, qty, motivo }
      );

      alert("Solicitação de produção criada com sucesso! Aguarde aprovação do Gestor.");
      setIsRequestModalOpen(false);
      setRequestSku("");
      setRequestQty("1");
      setRequestMotivo("");
      loadData();
    } catch (err) {
      console.error(err);
      alert("Erro ao solicitar produção: " + err.message);
    }
  }, [requestSku, requestQty, requestMotivo, activeOperator, user, loadData]);

  // --- EXCLUSÃO EM MASSA ---
  const handleBatchDelete = useCallback(async () => {
    if (selectedIds.size === 0) return;

    const confirmMessage = `PERIGO: Você tem certeza que deseja EXCLUIR PERMANENTEMENTE ${selectedIds.size} itens?`;
    if (!window.confirm(confirmMessage)) return;

    let previousItems = [];
    setItems((prev) => {
      previousItems = [...prev];
      return prev.filter(i => !selectedIds.has(i.id));
    });

    try {
      await stockProductionService.deleteBatch(Array.from(selectedIds));

      logAction(
        user,
        "ESTOQUE_PE",
        "EXCLUSAO_EM_MASSA",
        `Excluiu ${selectedIds.size} itens permanentemente`,
        { count: selectedIds.size, deletedIds: Array.from(selectedIds) }
      );

      setSelectedIds(new Set());
      loadData(); // Atualiza insumos/BOM
    } catch (err) {
      if (previousItems.length > 0) setItems(previousItems);
      console.error(err);
      alert("Erro ao excluir lote: " + err.message);
    }
  }, [selectedIds, user, loadData]);

  // --- AÇÕES ADICIONAIS CONFERÊNCIA ---
  const handleConfirmSkuUpload = useCallback(async (updatedItems) => {
    try {
      // 1. Para cada item, se o SKU foi alterado, atualiza na coleção inventory_items
      const updatePromises = updatedItems.map(async (ui) => {
        const originalItem = items.find(i => i.id === ui.id);
        if (originalItem && originalItem.sku !== ui.sku) {
          await pb.collection("inventory_items").update(ui.id, { sku: ui.sku });
          logAction(
            user,
            "ESTOQUE_PE",
            "ALTERAR_SKU",
            `Alterou SKU de ${originalItem.sku} para ${ui.sku} na finalização`,
            { itemId: ui.id, oldSku: originalItem.sku, newSku: ui.sku }
          );
        }
      });
      await Promise.all(updatePromises);

      // 2. Transiciona status de todos para in_stock
      const itemIds = updatedItems.map(ui => ui.id);
      await stockProductionService.advanceBatchStatus(itemIds, "in_stock", user?.name);

      logAction(
        user,
        "ESTOQUE_PE",
        "FINALIZAR_EM_MASSA",
        `Adicionou ${itemIds.length} itens ao estoque físico real`,
        { count: itemIds.length, itemIds }
      );

      setSelectedIds(new Set());
      setIsSkuModalOpen(false);
      setSkuModalItems([]);
      loadData(); // Atualiza insumos/BOM
      alert("Itens adicionados ao estoque com sucesso!");
    } catch (err) {
      alert("Erro ao enviar para o estoque: " + err.message);
    }
  }, [items, user, loadData]);

  const handleAdvanceSingleStatus = useCallback(async (itemId, newStatus) => {
    if (newStatus === "in_stock") {
      const item = items.find(i => i.id === itemId);
      if (item) {
        const catalogItem = findCatalogItem ? findCatalogItem(item.sku) : null;
        setSkuModalItems([{
          id: item.id,
          sku: item.sku || "",
          name: item.name || catalogItem?.name || "Produto sem cadastro",
          loteCodigo: item.loteCodigo || item.lote_codigo || item.lote || ""
        }]);
        setIsSkuModalOpen(true);
      }
      return;
    }

    const targetLabel = statuses.find(s => s.id === newStatus)?.label || newStatus;
    if (!window.confirm(`Deseja realmente mover este lote para a etapa "${targetLabel}"?`)) return;

    try {
      await stockProductionService.advanceBatchStatus([itemId], newStatus, user?.name);
      logAction(
        user,
        "ESTOQUE_PE",
        "ALTERAR_STATUS",
        `Alterou status do lote ${itemId} para ${newStatus}`,
        { itemId, targetStatus: newStatus }
      );
      loadData(); // Atualiza insumos/BOM
    } catch (err) {
      alert("Erro ao alterar status: " + err.message);
    }
  }, [items, user, loadData, statuses, findCatalogItem]);

  const handleBulkConferenciaAction = useCallback(async (newStatus) => {
    if (newStatus === "in_stock") {
      const itemsToConfirm = items.filter(i => selectedIds.has(i.id)).map(item => {
        const catalogItem = findCatalogItem ? findCatalogItem(item.sku) : null;
        return {
          id: item.id,
          sku: item.sku || "",
          name: item.name || catalogItem?.name || "Produto sem cadastro",
          loteCodigo: item.loteCodigo || item.lote_codigo || item.lote || ""
        };
      });
      if (itemsToConfirm.length > 0) {
        setSkuModalItems(itemsToConfirm);
        setIsSkuModalOpen(true);
      }
      return;
    }

    const actionLabel = "retornar para revisão";
    if (!window.confirm(`Deseja realmente ${actionLabel} os ${selectedIds.size} itens selecionados?`)) return;

    try {
      await stockProductionService.advanceBatchStatus(Array.from(selectedIds), newStatus, user?.name);
      logAction(
        user,
        "ESTOQUE_PE",
        "REVISAR_EM_MASSA",
        `Alterou status de ${selectedIds.size} lotes em massa para ${newStatus}`,
        { count: selectedIds.size, itemIds: Array.from(selectedIds), targetStatus: newStatus }
      );
      setSelectedIds(new Set());
      loadData(); // Atualiza insumos/BOM
    } catch (err) {
      alert("Erro ao processar alteração em massa: " + err.message);
    }
  }, [selectedIds, user, loadData, items, findCatalogItem]);

  // --- IMPRESSÃO ---
  const handleBatchPrint = () => {
    if (selectedIds.size === 0) return;

    const itemsToPrint = items.filter((i) => selectedIds.has(i.id));

    const adaptedItems = itemsToPrint.map((item) => {
      let loteName = item.loteCodigo || item.lote_codigo;
      if (!loteName && item.lote && !item.lote.match(/^[a-z0-9]{15}$/i)) {
        loteName = item.lote;
      }

      return {
        ...item,
        customerName: "PRODUCAO DE ESTOQUE",
        loteCodigo: loteName || "AVULSO",
        order: {
          number: item.sku || "SN",
        },
        dateStr: (() => {
          const val = item.dateIn || item.created;
          if (!val) return new Date().toLocaleDateString("pt-BR");
          try {
            const d = new Date(val);
            if (!isNaN(d.getTime())) return d.toLocaleDateString("pt-BR");
          } catch (e) {}
          return val;
        })(),
        specs: item.specs || {},
      };
    });

    const text = formatProductionTicket(adaptedItems);
    setPrintContent(text);
    setShowPrintModal(true);
  };

// --- SUBCOMPONENTE DASHBOARD DA FÁBRICA ---
function StockFactoryDashboard({ inventory, bomsList, suppliesList, findCatalogItem }) {
  // Estados para Filtro de Período
  const [periodType, setPeriodType] = useState("30d");
  const [customStart, setCustomStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split("T")[0];
  });
  const [customEnd, setCustomEnd] = useState(() => {
    return new Date().toISOString().split("T")[0];
  });

  // 1. Filtra itens ativos na produção (ignorando os estacionados em pré-escapulário)
  const activeItems = useMemo(() => {
    return inventory.filter(i => i.isPE === true && i.status !== "adjusted_out" && i.status !== "sold" && i.status !== "pe_interceptado" && i.status !== "pe_pre_escapulario" && i.situacao_fluxo !== "suspenso");
  }, [inventory]);

  // Helper para cálculo do custo do BOM
  const getSkuBomCost = useCallback((sku) => {
    if (!sku) return 0;
    const cleanSku = sku.trim().toUpperCase();
    
    // Busca exata ou hierárquica
    let recipe = bomsList.filter(b => b.sku === cleanSku);
    if (recipe.length === 0) {
      const parts = cleanSku.split("-");
      if (parts.length > 1) {
        for (let i = parts.length - 1; i >= 1; i--) {
          const parentSku = parts.slice(0, i).join("-");
          recipe = bomsList.filter(b => b.sku === parentSku);
          if (recipe.length > 0) break;
        }
      }
    }
    
    if (recipe.length === 0) return 0;
    
    let cost = 0;
    for (const item of recipe) {
      const supply = suppliesList.find(s => s.id === item.supply_id);
      if (supply && supply.sector && supply.sector.trim().toLowerCase() === "oficina") {
        cost += item.quantity_per_unit * suppliesService.getSupplyUnitCost(supply);
      }
    }
    return cost;
  }, [bomsList, suppliesList]);

  // 2. Calcula as datas de limite com base nos filtros
  const dateRange = useMemo(() => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    
    const start = new Date();
    start.setHours(0, 0, 0, 0);

    if (periodType === "today") {
      // Já está configurado para hoje
    } else if (periodType === "7d") {
      start.setDate(end.getDate() - 6);
    } else if (periodType === "15d") {
      start.setDate(end.getDate() - 14);
    } else if (periodType === "30d") {
      start.setDate(end.getDate() - 29);
    } else if (periodType === "custom") {
      if (customStart) {
        const s = new Date(customStart + "T00:00:00");
        if (!isNaN(s.getTime())) start.setTime(s.getTime());
      }
      if (customEnd) {
        const e = new Date(customEnd + "T23:59:59");
        if (!isNaN(e.getTime())) end.setTime(e.getTime());
      }
    }
    return { start, end };
  }, [periodType, customStart, customEnd]);

  // 3. Filtra itens produzidos no período selecionado
  const producedInPeriod = useMemo(() => {
    const { start, end } = dateRange;
    return inventory.filter(item => {
      if (item.isPE) return false;
      const history = Array.isArray(item.history) ? item.history : [];
      const finishedEvt = history.find(h => h.action === "finalizado_pe");
      if (finishedEvt && finishedEvt.date) {
        const d = new Date(finishedEvt.date);
        return d >= start && d <= end;
      }
      
      // Fallback
      if (item.status === "in_stock" || item.status === "sold" || item.status === "adjusted_out") {
        const date = item.updated || item.created || item.dateIn;
        if (date) {
          const d = new Date(date);
          return d >= start && d <= end;
        }
      }
      return false;
    });
  }, [inventory, dateRange]);

  // 4. Totais
  const activeCostTotal = useMemo(() => {
    return activeItems.reduce((acc, item) => acc + getSkuBomCost(item.sku), 0);
  }, [activeItems, getSkuBomCost]);

  const producedCostTotal = useMemo(() => {
    return producedInPeriod.reduce((acc, item) => acc + getSkuBomCost(item.sku), 0);
  }, [producedInPeriod, getSkuBomCost]);

  // 5. Agrupamento por Modelo (Lotes ativos)

  const modelStats = useMemo(() => {
    const map = {};
    activeItems.forEach(item => {
      const catalogItem = findCatalogItem ? findCatalogItem(item.sku) : null;
      const model = catalogItem?.model || catalogItem?.modelo || catalogItem?.full_data?.model || catalogItem?.full_data?.modelo || "Sem Modelo";
      if (!map[model]) map[model] = { count: 0, cost: 0 };
      map[model].count += 1;
      map[model].cost += getSkuBomCost(item.sku);
    });
    return Object.entries(map).map(([name, val]) => ({ name, ...val })).sort((a, b) => b.count - a.count);
  }, [activeItems, findCatalogItem, getSkuBomCost]);

  // 6. Insumos utilizados consolidados (Lotes ativos)
  const suppliesUsed = useMemo(() => {
    const map = {};
    activeItems.forEach(item => {
      const cleanSku = item.sku.trim().toUpperCase();
      let recipe = bomsList.filter(b => b.sku === cleanSku);
      if (recipe.length === 0) {
        const parts = cleanSku.split("-");
        if (parts.length > 1) {
          for (let i = parts.length - 1; i >= 1; i--) {
            const parentSku = parts.slice(0, i).join("-");
            recipe = bomsList.filter(b => b.sku === parentSku);
            if (recipe.length > 0) break;
          }
        }
      }
      
      recipe.forEach(recipeItem => {
        const supply = suppliesList.find(s => s.id === recipeItem.supply_id);
        if (supply && supply.sector && supply.sector.trim().toLowerCase() === "oficina") {
          const key = supply.id;
          if (!map[key]) {
            map[key] = {
              name: supply.name,
              sector: supply.sector || "Geral",
              unit: supply.unit || "un",
              quantity: 0,
              cost: 0
            };
          }
          map[key].quantity += recipeItem.quantity_per_unit;
          map[key].cost += recipeItem.quantity_per_unit * suppliesService.getSupplyUnitCost(supply);
        }
      });
    });
    return Object.values(map).sort((a, b) => b.cost - a.cost);
  }, [activeItems, bomsList, suppliesList]);

  // 7. Dados do Gráfico de Barras por dia
  const chartData = useMemo(() => {
    const list = [];
    const curr = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    
    // Proteção de loop infinito
    let limit = 0;
    while (curr <= end && limit < 100) {
      list.push(new Date(curr));
      curr.setDate(curr.getDate() + 1);
      limit++;
    }
    
    const grouped = {};
    producedInPeriod.forEach(item => {
      let dateVal = null;
      const history = Array.isArray(item.history) ? item.history : [];
      const finishedEvt = history.find(h => h.action === "finalizado_pe");
      if (finishedEvt && finishedEvt.date) {
        dateVal = new Date(finishedEvt.date);
      } else {
        const date = item.updated || item.created || item.dateIn;
        if (date) dateVal = new Date(date);
      }
      if (dateVal) {
        const yyyymmdd = dateVal.toISOString().split("T")[0];
        grouped[yyyymmdd] = (grouped[yyyymmdd] || 0) + 1;
      }
    });

    return list.map(d => {
      const yyyymmdd = d.toISOString().split("T")[0];
      const dateStr = d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
      const qty = grouped[yyyymmdd] || 0;
      return { dateStr, qty };
    });
  }, [dateRange, producedInPeriod]);

  const maxQty = useMemo(() => {
    return Math.max(...chartData.map(d => d.qty), 1);
  }, [chartData]);

  const formatBRL = (val) => {
    return Number(val || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  };

  return (
    <div className="p-4 space-y-6 overflow-y-auto h-full pb-20 custom-scrollbar bg-slate-50/30">
      
      {/* Filtros de Período */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="font-bold text-slate-700 text-sm">Filtros do Dashboard</h2>
          <p className="text-[10px] text-slate-400">Analise lotes finalizados no período escolhido</p>
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          {/* Botões Rápidos */}
          <div className="bg-slate-100 p-1 rounded-lg border flex gap-1 text-[11px] font-bold text-slate-600 w-full sm:w-auto justify-between">
            {[
              { id: "today", label: "Hoje" },
              { id: "7d", label: "7 Dias" },
              { id: "15d", label: "15 Dias" },
              { id: "30d", label: "30 Dias" },
              { id: "custom", label: "Personalizado" }
            ].map(opt => (
              <button
                key={opt.id}
                onClick={() => setPeriodType(opt.id)}
                className={`px-3 py-1 rounded transition-all grow text-center ${
                  periodType === opt.id
                    ? "bg-white text-slate-800 shadow-sm font-extrabold"
                    : "hover:text-slate-900"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Calendários Customizados */}
          {periodType === "custom" && (
            <div className="flex items-center gap-2 animate-fade-in text-xs w-full sm:w-auto">
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="px-2.5 py-1 border border-slate-300 rounded outline-none focus:border-purple-500 font-mono bg-white text-slate-700 shadow-inner"
              />
              <span className="text-slate-400 font-medium">até</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="px-2.5 py-1 border border-slate-300 rounded outline-none focus:border-purple-500 font-mono bg-white text-slate-700 shadow-inner"
              />
            </div>
          )}
        </div>
      </div>

      {/* Indicadores */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Lotes Ativos</span>
            <span className="text-2xl font-black text-slate-800">{activeItems.length}</span>
          </div>
          <div className="bg-purple-50 text-purple-600 p-2.5 rounded-lg">
            <Hammer size={18} />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Finalizados (Período)</span>
            <span className="text-2xl font-black text-slate-800">{producedInPeriod.length}</span>
          </div>
          <div className="bg-emerald-50 text-emerald-600 p-2.5 rounded-lg">
            <CheckCircle size={18} />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Custo Prod. Ativa</span>
            <span className="text-xl font-black text-slate-800">{formatBRL(activeCostTotal)}</span>
          </div>
          <div className="bg-amber-50 text-amber-600 p-2 rounded-lg text-xs font-black">R$</div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Custo Finalizado</span>
            <span className="text-xl font-black text-slate-800">{formatBRL(producedCostTotal)}</span>
          </div>
          <div className="bg-blue-50 text-blue-600 p-2 rounded-lg text-xs font-black">R$</div>
        </div>
      </div>

      {/* Gráfico de Barras da Produção ao longo do Período */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col w-full">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-bold text-slate-700 text-xs uppercase tracking-wider">
            📈 Peças Produzidas ao Longo do Período
          </h3>
          <span className="text-[10px] text-slate-400 font-bold bg-slate-100 border px-2 py-0.5 rounded">
            Total: {producedInPeriod.length} unidades
          </span>
        </div>
        
        <div className="flex items-end justify-between h-44 border-b border-l border-slate-200 pb-2 pl-2 mt-4 gap-1 w-full overflow-x-auto min-w-0">
          {chartData.map((d, idx) => {
            const heightPercent = (d.qty / maxQty) * 100;
            // Mostra apenas labels selecionadas se houver muitos dias para não embolar
            const showLabel = chartData.length <= 15 || idx % Math.ceil(chartData.length / 10) === 0 || idx === chartData.length - 1;
            
            return (
              <div key={idx} className="flex-1 flex flex-col items-center group relative h-full justify-end">
                {/* Tooltip */}
                <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute -top-8 bg-slate-800 text-white text-[9px] font-bold px-2 py-0.5 rounded shadow-lg pointer-events-none z-10 whitespace-nowrap">
                  {d.qty} un ({d.dateStr})
                </div>
                
                {/* Barra */}
                <div 
                  className="w-full bg-gradient-to-t from-purple-500 to-indigo-600 rounded-t hover:from-purple-600 hover:to-indigo-700 transition-all duration-300 relative cursor-pointer"
                  style={{ 
                    height: `${Math.max(heightPercent, d.qty > 0 ? 5 : 0)}%`,
                    minHeight: d.qty > 0 ? '4px' : '0px'
                  }}
                />
                
                {/* Label da Data */}
                <span className={`text-[8px] text-slate-400 font-mono mt-1 ${showLabel ? 'visible' : 'invisible'}`}>
                  {d.dateStr}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modelos */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col">
        <h3 className="font-bold text-slate-700 text-xs uppercase tracking-wider mb-3">💎 Produção Ativa por Modelo</h3>
        <div className="space-y-2 overflow-y-auto max-h-60 pr-1 custom-scrollbar">
          {modelStats.length === 0 ? (
            <p className="text-xs text-slate-400 italic text-center py-4">Nenhum lote ativo.</p>
          ) : (
            modelStats.map(stat => (
              <div key={stat.name} className="flex justify-between items-center text-xs font-bold border-b border-slate-100 pb-2">
                <span className="text-slate-600">{stat.name}</span>
                <div className="flex items-center gap-3">
                  <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full text-[10px] font-black">{stat.count} lotes</span>
                  <span className="text-slate-500 font-extrabold">{formatBRL(stat.cost)}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Insumos Utilizados */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
        <h3 className="font-bold text-slate-700 text-xs uppercase tracking-wider mb-3">🛠️ Insumos/Matérias-Primas BOM Utilizados (Produção Ativa)</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="border-b border-slate-200 text-slate-400 font-bold uppercase tracking-wider">
                <th className="py-2">Insumo</th>
                <th className="py-2">Setor</th>
                <th className="py-2 text-right">Qtd. Necessária</th>
                <th className="py-2 text-right">Custo Consolidado</th>
              </tr>
            </thead>
            <tbody>
              {suppliesUsed.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center py-6 text-slate-400 italic">Nenhum insumo associado aos lotes ativos.</td>
                </tr>
              ) : (
                suppliesUsed.map((supply, idx) => (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50 font-medium text-slate-600">
                    <td className="py-2 font-bold text-slate-800">{supply.name}</td>
                    <td className="py-2">
                      <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ${
                        supply.sector.toLowerCase() === "oficina" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"
                      }`}>
                        {supply.sector}
                      </span>
                    </td>
                    <td className="py-2 text-right font-mono font-bold">{supply.quantity.toFixed(2)} {supply.unit}</td>
                    <td className="py-2 text-right font-bold text-slate-800">{formatBRL(supply.cost)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

  const currentConfig =
    statuses.find((s) => s.id === activeTab) || statuses[0] || DEFAULT_STATUSES[0];

  return (
    <div className="flex flex-col md:flex-row h-[calc(100vh-64px)] w-full overflow-hidden bg-slate-50">
      {/* SIDEBAR */}
      <aside className="hidden md:flex w-64 bg-white border-r border-slate-200 flex-col shrink-0">
        <div className="p-4 border-b border-slate-100 shrink-0">
          <h2 className="font-bold text-slate-700 flex items-center gap-2">
            <Layers size={18} className="text-purple-600" /> Estoque Fábrica
          </h2>
        </div>
        
        {/* Lista de Statuses */}
        <div className="flex-1 p-2 space-y-1 overflow-y-auto custom-scrollbar flex flex-col justify-between">
          <div className="space-y-1">
            {/* Botão de Dashboard */}
            <div
              onClick={() => {
                setActiveTab("dashboard");
                setSelectedIds(new Set());
                setStoneColorFilter("all");
              }}
              className={`w-full text-left px-3 py-2.5 rounded-lg text-xs font-black flex items-center gap-2 transition-all cursor-pointer mb-2 border ${
                activeTab === "dashboard"
                  ? "bg-purple-600 text-white shadow-md border-purple-700"
                  : "text-purple-700 bg-purple-50 hover:bg-purple-100 border-purple-200"
              }`}
            >
              <BarChart2 size={16} />
              <span>Dashboard da Fábrica</span>
            </div>

            {/* Botão de Priorização de Produção */}
            <div
              onClick={() => {
                setActiveTab("prioridades");
                setSelectedIds(new Set());
                setStoneColorFilter("all");
              }}
              className={`w-full text-left px-3 py-2.5 rounded-lg text-xs font-black flex items-center gap-2 transition-all cursor-pointer mb-2 border ${
                activeTab === "prioridades"
                  ? "bg-amber-600 text-white shadow-md border-amber-700"
                  : "text-amber-800 bg-amber-50 hover:bg-amber-105 border-amber-200"
              }`}
            >
              <ClipboardList size={16} />
              <span>Prioridades de Produção</span>
            </div>

            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-3 mb-1">
              Etapas de Produção
            </div>

            {statuses.filter((s) => s.id !== "pe_conferencia").map((step) => (
              <div
                key={step.id}
                onClick={() => {
                  setActiveTab(step.id);
                  setSelectedIds(new Set());
                  setStoneColorFilter("all");
                }}
                className={`w-full text-left px-3 py-2.5 rounded-lg text-xs font-bold flex justify-between items-center transition-all cursor-pointer group/item ${activeTab === step.id
                    ? `bg-slate-800 text-white shadow-md`
                    : "text-slate-600 hover:bg-slate-100"
                  }`}
              >
                <span className="truncate pr-1">{step.label}</span>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span
                    className={`px-1.5 py-0.5 rounded text-[9px] ${activeTab === step.id
                        ? "bg-white/20 text-white"
                        : "bg-slate-200 text-slate-600"
                      }`}
                  >
                    {counts[step.id] || 0}
                  </span>
                  
                  {/* Botão de Excluir Status */}
                  <button
                    onClick={(e) => handleRemoveStatus(step.id, e)}
                    title={counts[step.id] > 0 ? "Não é possível remover (contém itens)" : "Remover status"}
                    disabled={counts[step.id] > 0}
                    className={`p-1 rounded hover:bg-red-500 hover:text-white transition-all ${
                      counts[step.id] > 0 
                        ? "opacity-20 cursor-not-allowed text-slate-400" 
                        : "opacity-0 group-hover/item:opacity-100 text-slate-400"
                    }`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Destaque para a Conferência de Lotes */}
          <div className="border-t border-slate-200 pt-3 px-1 mt-auto">
            {(() => {
              const confStep = statuses.find(s => s.id === "pe_conferencia") || DEFAULT_STATUSES.find(s => s.id === "pe_conferencia");
              return (
                <div
                  onClick={() => {
                    setActiveTab("pe_conferencia");
                    setSelectedIds(new Set());
                    setStoneColorFilter("all");
                  }}
                  className={`w-full text-left px-3 py-3 rounded-lg text-xs font-black flex justify-between items-center transition-all cursor-pointer relative shadow-sm border ${
                    activeTab === "pe_conferencia"
                      ? "bg-teal-600 text-white border-teal-700"
                      : "bg-teal-50 text-teal-800 border-teal-200 hover:bg-teal-100"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span className="text-sm">🔍</span>
                    <span>{confStep.label}</span>
                  </span>
                  <span
                    className={`px-1.5 py-0.5 rounded text-[10px] font-black ${
                      activeTab === "pe_conferencia"
                        ? "bg-white/20 text-white"
                        : "bg-teal-200 text-teal-800"
                    }`}
                  >
                    {counts["pe_conferencia"] || 0}
                  </span>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Formulário para Adicionar Status */}
        <div className="p-3 border-t border-slate-100 bg-slate-50 shrink-0">
          <form onSubmit={handleAddStatus} className="flex gap-1 items-center">
            <input
              type="text"
              placeholder="Novo status..."
              value={newStatusLabel}
              onChange={(e) => setNewStatusLabel(e.target.value)}
              className="flex-1 px-2.5 py-1.5 border border-slate-300 rounded text-[11px] outline-none focus:border-purple-500 bg-white font-medium"
            />
            <button
              type="submit"
              className="bg-purple-600 text-white p-1.5 rounded hover:bg-purple-700 transition-colors shadow-sm"
              title="Adicionar Novo Status"
            >
              <Plus size={14} />
            </button>
          </form>
        </div>
      </aside>

      {/* MENU MOBILE */}
      <div className="md:hidden bg-slate-100 border-b border-slate-200 p-3 shrink-0 z-10">
        <label className="text-[10px] font-bold text-slate-500 uppercase mb-1 flex items-center gap-1">
          <Layers size={12} /> Filtrar Etapa:
        </label>
        <div className="relative">
          <select
            value={activeTab}
            onChange={(e) => {
              setActiveTab(e.target.value);
              setSelectedIds(new Set());
              setStoneColorFilter("all");
            }}
            className="w-full appearance-none bg-white border border-slate-300 text-slate-800 text-sm font-bold rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-purple-500 shadow-sm"
          >
            <option value="dashboard">📊 Dashboard da Fábrica</option>
            <option value="prioridades">📋 Prioridades de Produção</option>
            <option value="lotes">📦 Lotes de Produção</option>
            {(user?.role === "master" || user?.role === "gestor_estoque") && (
              <option value="config_motivos">⚙️ Configurações de Motivos</option>
            )}
            {statuses.map((status) => (
              <option key={status.id} value={status.id}>
                {status.id === "pe_conferencia" ? "🔍 " : ""}{status.label} ({counts[status.id] || 0})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* PRIMARY AREA */}
      <main className="flex-1 flex flex-col min-w-0 relative">
        <div className="bg-white border-b border-slate-200 p-4 shrink-0 space-y-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="hidden md:block">
              <h1 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <span
                  className={`w-3 h-3 rounded-full ${activeTab === "dashboard" ? "bg-purple" : activeTab === "prioridades" ? "bg-amber" : (currentConfig?.bg ? currentConfig.bg.replace("bg-", "") : "purple")}-500`}
                ></span>
                {activeTab === "dashboard" 
                  ? "Dashboard da Fábrica" 
                  : activeTab === "prioridades"
                    ? "Prioridades de Produção"
                    : (currentConfig?.label || "Fábrica")
                }
              </h1>
              <p className="text-xs text-slate-500">
                {activeTab === "dashboard" 
                  ? "Indicadores gerais de pronta entrega" 
                  : activeTab === "prioridades"
                    ? "Gestão e priorização da fila de fabricação"
                    : "Gestão de lotes (Pronta Entrega)"
                }
              </p>
            </div>

            {activeTab !== "dashboard" && activeTab !== "lotes" && activeTab !== "config_motivos" && (
              <div className="flex flex-col w-full md:flex-row gap-2 md:w-auto items-center">
                {/* FORM */}
                {activeTab !== "prioridades" && (
                  !isOficina ? (
                    <form
                      onSubmit={handleAddItem}
                      className="flex gap-1 items-center bg-slate-50 p-1 rounded-lg border border-slate-200 shadow-sm w-full md:w-auto"
                    >
                      <div className="relative flex-1 md:flex-none">
                        <PackagePlus
                          className="absolute left-2 top-1/2 -translate-y-1/2 text-purple-400"
                          size={14}
                        />
                        <input
                          className="pl-7 pr-2 py-1.5 border border-slate-300 rounded text-xs outline-none focus:border-purple-500 w-full md:w-28 uppercase font-bold bg-white"
                          placeholder="SKU..."
                          value={newSku}
                          onChange={(e) => setNewSku(e.target.value)}
                        />
                      </div>
                      <div className="relative w-20 md:w-auto">
                        <Hash
                          className="absolute left-2 top-1/2 -translate-y-1/2 text-purple-400"
                          size={12}
                        />
                        <input
                          type="number"
                          min="1"
                          className="pl-6 pr-1 py-1.5 border border-slate-300 rounded text-xs outline-none focus:border-purple-500 w-full md:w-14 font-bold text-center bg-white"
                          value={newQty}
                          onChange={(e) => setNewQty(e.target.value)}
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={isAdding}
                        className="bg-purple-600 text-white p-1.5 rounded hover:bg-purple-700 shadow-sm disabled:opacity-50 transition-colors"
                      >
                        {isAdding ? (
                          <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                        ) : (
                          <Plus size={16} />
                        )}
                      </button>
                    </form>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setIsRequestModalOpen(true)}
                      className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white font-bold py-1.5 px-3 rounded text-xs shadow-sm transition-all"
                    >
                      <Plus size={14} /> Solicitar Produção
                    </button>
                  )
                )}

                {/* FILTRO COR DE PEDRA */}
                <div className="relative w-full md:w-40">
                  <select
                    value={stoneColorFilter}
                    onChange={(e) => setStoneColorFilter(e.target.value)}
                    className="w-full bg-white border border-slate-300 text-slate-700 text-xs font-bold rounded-lg p-2 outline-none focus:ring-2 focus:ring-purple-500 shadow-sm cursor-pointer"
                  >
                    <option value="all">Pedra: Todas</option>
                    {availableColors.map(color => (
                      <option key={color} value={color}>{color}</option>
                    ))}
                  </select>
                </div>

                {/* FILTER TEXTO */}
                <div className="relative w-full md:w-48">
                  <Search
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    size={14}
                  />
                  <input
                    className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm outline-none focus:border-blue-500"
                    placeholder="Filtrar SKU..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          {/* BULK ACTIONS */}
          {activeTab !== "dashboard" && activeTab !== "prioridades" && activeTab !== "lotes" && activeTab !== "config_motivos" && selectedIds.size > 0 && (
            <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 bg-purple-50 border border-purple-100 p-2 rounded-lg animate-fade-in shadow-sm">
              <div className="flex items-center gap-2 px-2 md:border-r border-purple-200 mb-2 md:mb-0">
                <BoxSelect size={18} className="text-purple-600" />
                <span className="text-xs font-bold text-purple-900">
                  {selectedIds.size} selecionados
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2 flex-1">
                {activeTab !== "pe_conferencia" && (
                  <button
                    onClick={handleBatchDelete}
                    className="bg-red-100 text-red-700 hover:bg-red-600 hover:text-white px-3 py-1.5 rounded text-xs font-bold transition-colors flex items-center gap-2 border border-red-200 hover:border-red-600 grow md:grow-0 justify-center"
                  >
                    <Trash2 size={14} /> <span className="md:hidden">Excluir</span>
                  </button>
                )}

                <button
                  onClick={handleBatchPrint}
                  className="bg-slate-800 text-white px-3 py-1.5 rounded text-xs font-bold hover:bg-slate-900 flex items-center gap-2 shadow-sm transition-colors grow md:grow-0 justify-center"
                >
                  <Printer size={14} /> <span className="md:hidden">Imprimir</span>
                </button>

                {activeTab === "pe_conferencia" ? (
                  <div className="flex items-center gap-2 grow md:grow-0">
                    <button
                      onClick={() => handleBulkConferenciaAction("in_stock")}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded text-xs font-bold shadow-sm transition-all flex items-center gap-1 cursor-pointer"
                    >
                      <CheckCircle size={14} /> Adicionar ao Estoque
                    </button>
                    <button
                      onClick={() => handleBulkConferenciaAction("pe_revisao")}
                      className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded text-xs font-bold shadow-sm transition-all flex items-center gap-1 cursor-pointer"
                    >
                      <RotateCcw size={14} /> Voltar para Revisão
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 w-full md:w-auto md:ml-auto mt-2 md:mt-0">
                    <span className="text-xs text-purple-700 font-medium hidden md:inline">
                      Mover:
                    </span>
                    <select
                      className="p-1.5 rounded border border-purple-200 text-xs font-bold text-slate-700 outline-none flex-1 md:w-40 bg-white cursor-pointer"
                      value={targetStatus}
                      onChange={(e) => setTargetStatus(e.target.value)}
                    >
                      <option value="">Destino...</option>
                      {statuses.filter((s) => s.id !== activeTab).map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.label}
                        </option>
                      ))}
                    </select>

                    <button
                      onClick={handleBulkMove}
                      disabled={!targetStatus}
                      className="bg-purple-600 text-white px-4 py-1.5 rounded text-xs font-bold hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2 shadow-sm transition-colors"
                    >
                      <ArrowRight size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* PRIMARY VIEW CONTENT */}
        {activeTab === "prioridades" ? (
          <StockFactoryPriorities
            items={items}
            setItems={setItems}
            findCatalogItem={findCatalogItem}
            user={user}
            statuses={statuses}
            getItemStatus={getItemStatus}
            parseStatusId={parseStatusId}
            searchTerm={searchTerm}
            stoneColorFilter={stoneColorFilter}
            getStoneColor={getStoneColor}
          />
        ) : activeTab === "dashboard" ? (
          <StockFactoryDashboard
            inventory={inventory}
            bomsList={bomsList}
            suppliesList={suppliesList}
            findCatalogItem={findCatalogItem}
          />
        ) : activeTab === "lotes" ? (
          <LotesTab
            user={user}
            inventory={inventory}
            bomsList={bomsList}
            suppliesList={suppliesList}
            findCatalogItem={findCatalogItem}
          />
        ) : activeTab === "config_motivos" ? (
          <MotivosConfigTab
            user={user}
          />
        ) : (
          /* LIST VIEW */
          <div className="flex-1 overflow-y-auto p-2 md:p-4 custom-scrollbar bg-slate-50/50">
            <div className="flex items-center gap-4 px-2 md:px-4 py-2 mb-2 text-xs font-bold text-slate-400 uppercase">
              <button
                onClick={toggleSelectAll}
                className="flex items-center gap-2 hover:text-slate-600 transition-colors"
              >
                {selectedIds.size > 0 &&
                  selectedIds.size === filteredItems.length ? (
                  <CheckSquare size={16} className="text-purple-600" />
                ) : (
                  <Square size={16} />
                )}
                Selecionar Todos
              </button>
              <span className="hidden md:inline">Detalhes do Item</span>
            </div>

            <div className="space-y-6 pb-20">
              {sortedColors.map((color) => {
                const colorItems = groupedByColor[color];
                const styles = getStoneColorStyles(color);
                
                return (
                  <div key={color} className={`${styles.bg} rounded-xl border ${styles.border} shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md`}>
                    {/* Cabeçalho do Grupo de Cor de Pedra */}
                    <div className={`border-b ${styles.border} px-4 py-2.5 flex justify-between items-center bg-white/40`}>
                      <div className="flex items-center gap-2">
                        <span className={`w-2.5 h-2.5 rounded-full ${styles.dot}`}></span>
                        <h3 className={`text-xs font-black uppercase tracking-wider ${styles.text}`}>
                          Pedra: <span className="font-bold normal-case text-sm ml-0.5">{color}</span>
                        </h3>
                      </div>
                      <span className={`bg-white/60 ${styles.text} text-[10px] font-extrabold px-2.5 py-0.5 rounded-full border ${styles.border}`}>
                        {colorItems.length} {colorItems.length === 1 ? "lote" : "lotes"}
                      </span>
                    </div>
                    
                    {/* Lista de Itens do Grupo */}
                    <div className="p-2 space-y-1.5 bg-white/10">
                      {colorItems.map((item) => {
                        const catalogItem = findCatalogItem
                          ? findCatalogItem(item.sku)
                          : null;
                        const isSelected = selectedIds.has(item.id);

                        return (
                          <StockProductionItemRow
                            key={item.id}
                            item={item}
                            catalogItem={catalogItem}
                            isSelected={isSelected}
                            currentConfig={currentConfig}
                            onToggleSelect={toggleSelectOne}
                            onDelete={handleDelete}
                            onSuspend={handleOpenSuspendModal}
                            onReactivate={handleReactivateItem}
                            isLoteCancelado={item.lote && canceledLoteIds.has(item.lote)}
                            onConvertLoteToAvulso={handleConvertLoteToAvulso}
                            bomSkuSet={bomSkuSet}
                            user={user}
                            onAdvanceStatus={handleAdvanceSingleStatus}
                            isOficina={isOficina}
                            availableStatuses={statuses}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {filteredItems.length === 0 && (
                <div className="text-center py-10 text-slate-400 animate-fade-in bg-white rounded-xl border border-slate-200">
                  <Layers size={48} className="mx-auto mb-2 opacity-20" />
                  <p>Nenhum item nesta etapa.</p>
                  {activeTab === "pe_solicitado" && (
                    <p className="text-xs text-purple-500 mt-2 font-medium">
                      Use o campo acima para criar novos lotes!
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* MODAL */}
      {showPrintModal && (
        <TextModal
          title={`Etiquetas de Estoque (${selectedIds.size})`}
          content={printContent}
          onClose={() => {
            setShowPrintModal(false);
            setPrintContent("");
          }}
        />
      )}

      {isSkuModalOpen && (
        <SkuConfirmationModal
          isOpen={isSkuModalOpen}
          items={skuModalItems}
          onClose={() => {
            setIsSkuModalOpen(false);
            setSkuModalItems([]);
          }}
          onConfirm={handleConfirmSkuUpload}
        />
      )}

      {/* MODAL SOLICITAR PRODUÇÃO (OFICINA) */}
      {isRequestModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <form 
            onSubmit={handleRequestSubmit}
            className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 border border-slate-100 flex flex-col space-y-4 animate-in fade-in zoom-in-95 duration-200"
          >
            <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span>
              Solicitar Produção à Fábrica
            </h3>
            
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase mb-1">SKU:</label>
              <input
                className="w-full border border-slate-300 rounded-lg p-2.5 text-sm uppercase font-bold outline-none focus:ring-2 focus:ring-amber-500"
                placeholder="Ex: ANL-01..."
                value={requestSku}
                onChange={(e) => setRequestSku(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Quantidade:</label>
              <input
                type="number"
                min="1"
                className="w-full border border-slate-300 rounded-lg p-2.5 text-sm font-bold outline-none focus:ring-2 focus:ring-amber-500"
                value={requestQty}
                onChange={(e) => setRequestQty(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Justificativa / Motivo da Solicitação:</label>
              <textarea
                rows="3"
                className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-amber-500"
                placeholder="Descreva o motivo pelo qual precisa produzir este item..."
                value={requestMotivo}
                onChange={(e) => setRequestMotivo(e.target.value)}
                required
              ></textarea>
            </div>

            <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-200 text-[11px] text-slate-500">
              <span className="font-bold">Carimbo do Operador:</span> {activeOperator?.nome || user?.name || "Oficina"}
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <button
                type="button"
                onClick={() => {
                  setIsRequestModalOpen(false);
                  setRequestSku("");
                  setRequestQty("1");
                  setRequestMotivo("");
                }}
                className="bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold py-2 px-4 rounded text-xs transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-2 px-4 rounded text-xs shadow-sm transition-colors"
              >
                Confirmar Solicitação
              </button>
            </div>
          </form>
        </div>
      )}

      {/* MODAL SUSPENDER PEÇA (CONTRATO DE VOLTA BOM) */}
      {isSuspendModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <form 
            onSubmit={handleSuspendConfirm}
            className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 border border-slate-100 flex flex-col space-y-4 animate-in fade-in zoom-in-95 duration-200"
          >
            <h3 className="text-sm font-black text-red-600 uppercase tracking-wider flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-red-600"></span>
              Suspender Produção da Peça
            </h3>
            
            <p className="text-xs text-slate-500 leading-relaxed">
              Ao suspender esta peça, o fluxo de fabricação dela será interrompido permanentemente. Os insumos correspondentes da oficina serão devolvidos ao estoque e a peça ficará salva no histórico de auditoria do lote.
            </p>

            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Motivo da Suspensão:</label>
              <select
                className="w-full border border-slate-300 bg-white rounded-lg p-2.5 text-xs font-bold outline-none focus:ring-2 focus:ring-red-500 cursor-pointer"
                value={suspendMotivoId}
                onChange={(e) => setSuspendMotivoId(e.target.value)}
                required
              >
                <option value="">Selecione...</option>
                {suspensaoMotivosList.map(motivo => (
                  <option key={motivo.id} value={motivo.id}>{motivo.nome}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase mb-1">
                Observações/Justificativa: 
                {suspensaoMotivosList.find(m => m.id === suspendMotivoId)?.obs_obrigatoria && (
                  <span className="text-red-500 lowercase font-normal ml-1">(Obrigatório para este motivo)</span>
                )}
              </label>
              <textarea
                rows="3"
                className="w-full border border-slate-300 rounded-lg p-2.5 text-xs outline-none focus:ring-2 focus:ring-red-500"
                placeholder="Forneça detalhes adicionais sobre o descarte/suspensão..."
                value={suspendObs}
                onChange={(e) => setSuspendObs(e.target.value)}
                required={suspensaoMotivosList.find(m => m.id === suspendMotivoId)?.obs_obrigatoria}
              ></textarea>
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <button
                type="button"
                onClick={() => {
                  setIsSuspendModalOpen(false);
                  setSuspendItemId(null);
                  setSuspendMotivoId("");
                  setSuspendObs("");
                }}
                className="bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold py-2 px-4 rounded text-xs transition-colors"
              >
                Voltar
              </button>
              <button
                type="submit"
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded text-xs shadow-sm transition-colors"
              >
                Confirmar Suspensão
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// --- SUBCOMPONENTE DE PRIORIDADES DE PRODUÇÃO ---
function StockFactoryPriorities({
  items,
  setItems,
  findCatalogItem,
  user,
  statuses,
  getItemStatus,
  parseStatusId,
  searchTerm,
  stoneColorFilter,
  getStoneColor,
}) {
  // Filtro e agrupamento dos itens
  const filteredAndGrouped = useMemo(() => {
    const list = items.filter(item => {
      // Filtro de termo de busca
      const matchesSearch = !searchTerm || 
        item.sku?.toLowerCase().includes(searchTerm.toLowerCase()) || 
        item.name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (!matchesSearch) return false;
      
      // Filtro de cor de pedra
      const catalogItem = findCatalogItem ? findCatalogItem(item.sku) : null;
      const matchesColor = stoneColorFilter === "all" || getStoneColor(catalogItem) === stoneColorFilter;
      
      return matchesColor;
    });

    const groups = {
      Planejado: [],
      Prioritario: [],
      Urgente: []
    };

    list.forEach(item => {
      const prio = item.full_data?.prioridade;
      if (prio === "Urgente") {
        groups.Urgente.push(item);
      } else if (prio === "Prioritario" || prio === "Prioritário") {
        groups.Prioritario.push(item);
      } else {
        groups.Planejado.push(item);
      }
    });

    return groups;
  }, [items, searchTerm, stoneColorFilter, findCatalogItem, getStoneColor]);

  // Função amigável para obter o rótulo do status
  const getFriendlyStatusLabel = (item) => {
    const statusId = getItemStatus(item);
    const st = statuses.find(s => s.id === statusId);
    if (st) return st.label;
    return parseStatusId(statusId) || statusId;
  };

  // Função para obter classes de estilo da etapa
  const getFriendlyStatusClass = (item) => {
    const statusId = getItemStatus(item);
    const st = statuses.find(s => s.id === statusId);
    if (st) return `${st.bg} ${st.text}`;
    return "bg-slate-100 text-slate-700";
  };

  // Função para salvar nova prioridade
  const handleUpdatePriority = async (itemId, newPriority) => {
    try {
      const item = items.find(i => i.id === itemId);
      if (!item) return;
      const currentFullData = item.full_data || {};
      const updatedFullData = {
        ...currentFullData,
        prioridade: newPriority
      };

      await pb.collection("inventory_items").update(itemId, {
        full_data: updatedFullData
      });

      // Atualiza o estado local para atualizar a interface imediatamente
      setItems(prev => prev.map(i => i.id === itemId ? { ...i, full_data: updatedFullData } : i));
      
      try {
        await logAction(
          "update_priority",
          `Prioridade de ${item.sku} alterada para ${newPriority}`,
          user?.name || "Sistema"
        );
      } catch (logErr) {
        console.warn("Erro ao registrar log de prioridade:", logErr);
      }
    } catch (err) {
      console.error("Erro ao salvar nova prioridade:", err);
    }
  };

  return (
    <div className="flex-1 flex flex-col p-4 overflow-y-auto bg-slate-50/50">
      
      {/* Resumo de Contagem */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Planejado</span>
            <p className="text-xl font-black text-slate-750 mt-0.5">{filteredAndGrouped.Planejado.length} itens</p>
          </div>
          <div className="p-2 bg-slate-100 text-slate-500 rounded-lg">
            <ClipboardList size={18} />
          </div>
        </div>
        <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[10px] font-bold text-orange-400 uppercase tracking-wider">Prioritário</span>
            <p className="text-xl font-black text-orange-700 mt-0.5">{filteredAndGrouped.Prioritario.length} itens</p>
          </div>
          <div className="p-2 bg-orange-50 text-orange-500 rounded-lg">
            <TrendingUp size={18} />
          </div>
        </div>
        <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[10px] font-bold text-red-500 uppercase tracking-wider animate-pulse">Urgente</span>
            <p className="text-xl font-black text-red-650 mt-0.5">{filteredAndGrouped.Urgente.length} itens</p>
          </div>
          <div className="p-2 bg-red-50 text-red-500 rounded-lg">
            <Flame size={18} />
          </div>
        </div>
      </div>

      {/* Grid de Colunas de Prioridades */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6 items-start min-h-0">
        
        {/* COLUNA: PLANEJADO */}
        <div className="flex flex-col bg-slate-100/50 p-4 rounded-xl border border-slate-200 min-h-[400px] h-full">
          <div className="flex items-center gap-2 mb-4 border-b border-slate-200 pb-2">
            <div className="w-2.5 h-2.5 rounded-full bg-slate-400"></div>
            <strong className="text-xs uppercase font-black text-slate-600 tracking-wider">Planejado</strong>
            <span className="ml-auto bg-slate-200 text-slate-600 font-bold px-1.5 py-0.5 rounded text-[10px]">
              {filteredAndGrouped.Planejado.length}
            </span>
          </div>
          
          <div className="flex-1 overflow-y-auto space-y-3 custom-scrollbar pr-1 max-h-[500px]">
            {filteredAndGrouped.Planejado.map(item => (
              <PriorityItemCard 
                key={item.id} 
                item={item} 
                getFriendlyStatusLabel={getFriendlyStatusLabel}
                getFriendlyStatusClass={getFriendlyStatusClass}
                findCatalogItem={findCatalogItem}
                handleUpdatePriority={handleUpdatePriority}
              />
            ))}
            {filteredAndGrouped.Planejado.length === 0 && (
              <p className="text-[11px] text-slate-450 italic text-center py-8">Nenhum item planejado.</p>
            )}
          </div>
        </div>

        {/* COLUNA: PRIORITÁRIO */}
        <div className="flex flex-col bg-orange-50/20 p-4 rounded-xl border border-orange-200 min-h-[400px] h-full">
          <div className="flex items-center gap-2 mb-4 border-b border-orange-200 pb-2">
            <div className="w-2.5 h-2.5 rounded-full bg-orange-400"></div>
            <strong className="text-xs uppercase font-black text-orange-700 tracking-wider">Prioritário</strong>
            <span className="ml-auto bg-orange-100 text-orange-850 font-bold px-1.5 py-0.5 rounded text-[10px]">
              {filteredAndGrouped.Prioritario.length}
            </span>
          </div>
          
          <div className="flex-1 overflow-y-auto space-y-3 custom-scrollbar pr-1 max-h-[500px]">
            {filteredAndGrouped.Prioritario.map(item => (
              <PriorityItemCard 
                key={item.id} 
                item={item} 
                getFriendlyStatusLabel={getFriendlyStatusLabel}
                getFriendlyStatusClass={getFriendlyStatusClass}
                findCatalogItem={findCatalogItem}
                handleUpdatePriority={handleUpdatePriority}
              />
            ))}
            {filteredAndGrouped.Prioritario.length === 0 && (
              <p className="text-[11px] text-slate-450 italic text-center py-8">Nenhum item prioritário.</p>
            )}
          </div>
        </div>

        {/* COLUNA: URGENTE */}
        <div className="flex flex-col bg-red-50/10 p-4 rounded-xl border border-red-200 min-h-[400px] h-full">
          <div className="flex items-center gap-2 mb-4 border-b border-red-200 pb-2">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-ping"></div>
            <strong className="text-xs uppercase font-black text-red-650 tracking-wider">Urgente</strong>
            <span className="ml-auto bg-red-100 text-red-700 font-bold px-1.5 py-0.5 rounded text-[10px]">
              {filteredAndGrouped.Urgente.length}
            </span>
          </div>
          
          <div className="flex-1 overflow-y-auto space-y-3 custom-scrollbar pr-1 max-h-[500px]">
            {filteredAndGrouped.Urgente.map(item => (
              <PriorityItemCard 
                key={item.id} 
                item={item} 
                getFriendlyStatusLabel={getFriendlyStatusLabel}
                getFriendlyStatusClass={getFriendlyStatusClass}
                findCatalogItem={findCatalogItem}
                handleUpdatePriority={handleUpdatePriority}
              />
            ))}
            {filteredAndGrouped.Urgente.length === 0 && (
              <p className="text-[11px] text-slate-450 italic text-center py-8">Nenhum item urgente.</p>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

// Subcomponente de Card de Item
function PriorityItemCard({ 
  item, 
  getFriendlyStatusLabel, 
  getFriendlyStatusClass, 
  findCatalogItem, 
  handleUpdatePriority 
}) {
  const catalogItem = findCatalogItem ? findCatalogItem(item.sku) : null;
  const name = catalogItem?.name || item.name || "Sem Nome";
  const model = catalogItem?.model || catalogItem?.modelo || catalogItem?.full_data?.model || catalogItem?.full_data?.modelo || "Sem Modelo";

  return (
    <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm flex flex-col gap-2 hover:border-slate-350 hover:shadow-md transition-all">
      <div className="flex justify-between items-start">
        <strong className="text-xs font-mono font-bold text-slate-800 break-all">{item.sku}</strong>
        <span className={`px-1.5 py-0.5 rounded text-[8px] border font-black uppercase tracking-wider ${getFriendlyStatusClass(item)}`}>
          {getFriendlyStatusLabel(item)}
        </span>
      </div>
      
      <div className="text-[11px]">
        <p className="text-slate-650 line-clamp-1 font-medium">{name}</p>
        <p className="text-slate-400 text-[9px] font-semibold">{model}</p>
      </div>

      <div className="border-t border-slate-100 pt-2 flex items-center justify-between gap-2">
        <span className="text-[9px] text-slate-450 font-bold uppercase">Prioridade:</span>
        <select
          value={item.full_data?.prioridade || "Planejado"}
          onChange={(e) => handleUpdatePriority(item.id, e.target.value)}
          className={`px-1.5 py-0.5 border rounded text-[10px] font-bold uppercase tracking-wider outline-none bg-white cursor-pointer ${
            item.full_data?.prioridade === "Urgente" 
              ? "bg-red-50 text-red-700 border-red-300"
              : item.full_data?.prioridade === "Prioritario" 
                ? "bg-orange-50 text-orange-850 border-orange-200"
                : "bg-slate-50 text-slate-600 border-slate-200"
          }`}
        >
          <option value="Planejado">Planejado</option>
          <option value="Prioritario">Prioritário</option>
          <option value="Urgente">Urgente</option>
        </select>
      </div>
    </div>
  );
}
